import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { xtreamApi } from '../services/xtreamApi';
import ContentCard from '../components/Common/ContentCard';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { ScrollArea } from '../components/ui/scroll-area';
import { MonitorPlay, ChevronRight, Search, Loader2, ArrowDownAZ, ArrowUpZA, Clock, AlertTriangle } from 'lucide-react';
import { Input } from '../components/ui/input';

const ITEMS_PER_PAGE = 30;

const SORT_OPTIONS = [
  { key: 'az', label: 'A - Z', icon: ArrowDownAZ },
  { key: 'za', label: 'Z - A', icon: ArrowUpZA },
  { key: 'newest', label: 'Newest', icon: Clock },
];

const SeriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [allSeries, setAllSeries] = useState([]);
  const [displayedSeries, setDisplayedSeries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingSeries, setIsLoadingSeries] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('az');
  
  const loadMoreRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const data = await xtreamApi.getSeriesCategories();
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!selectedCategory) {
      setAllSeries([]);
      setDisplayedSeries([]);
      setHasMore(false);
      return;
    }

    const fetchSeries = async () => {
      try {
        setIsLoadingSeries(true);
        setPage(1);
        const data = await xtreamApi.getSeries(selectedCategory.category_id);
        const series = Array.isArray(data) ? data : [];
        setAllSeries(series);
      } catch (err) {
        console.error('Error fetching series:', err);
        setAllSeries([]);
        setDisplayedSeries([]);
      } finally {
        setIsLoadingSeries(false);
      }
    };
    fetchSeries();
  }, [selectedCategory]);

  const sortedSeries = useMemo(() => {
    const sorted = [...allSeries];
    switch (sortBy) {
      case 'az':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'za':
        sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'newest':
        sorted.sort((a, b) => parseInt(b.last_modified || '0') - parseInt(a.last_modified || '0'));
        break;
      default:
        break;
    }
    return sorted;
  }, [allSeries, sortBy]);

  useEffect(() => {
    if (sortedSeries.length === 0) return;
    setPage(1);
    setDisplayedSeries(sortedSeries.slice(0, ITEMS_PER_PAGE));
    setHasMore(sortedSeries.length > ITEMS_PER_PAGE);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [sortedSeries]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    const nextPage = page + 1;
    const endIndex = nextPage * ITEMS_PER_PAGE;
    const newSeries = sortedSeries.slice(0, endIndex);
    
    setTimeout(() => {
      setDisplayedSeries(newSeries);
      setPage(nextPage);
      setHasMore(endIndex < sortedSeries.length);
      setIsLoadingMore(false);
    }, 100);
  }, [page, sortedSeries, hasMore, isLoadingMore]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasMore || isLoadingMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 300) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const filteredCategories = categories.filter(cat =>
    cat.category_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoadingCategories) {
    return (
      <div data-testid="series-loading" className="min-h-screen bg-[#0a0a0b] flex items-center justify-center pt-16">
        <LoadingSpinner size="large" text="Loading categories..." />
      </div>
    );
  }

  return (
    <div data-testid="series-page" className="min-h-screen bg-[#0a0a0b] pt-16">
      <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2 text-amber-400 text-xs">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
        <span>Due to browser limitations, you may experience no audio from some titles. For better compatibility please use on a dedicated streaming device.</span>
      </div>
      <div className="flex h-[calc(100vh-64px-32px)]">
        {/* Categories Sidebar */}
        <div data-testid="series-category-sidebar" className="w-72 flex-shrink-0 bg-[#0d0d0f] border-r border-gray-800">
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-white font-semibold flex items-center gap-2 mb-3">
              <MonitorPlay style={{color:"var(--accent)"}} className="w-5 h-5" />
              Series Categories
            </h2>
            <p className="text-gray-500 text-xs mb-3">{categories.length} categories</p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                data-testid="series-category-search"
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-[#1a1a1d] border-gray-700 text-white text-sm"
              />
            </div>
          </div>
          
          <ScrollArea className="h-[calc(100%-140px)]">
            <div className="p-2">
              {filteredCategories.map((category) => (
                <button
                  key={category.category_id}
                  data-testid={`series-category-${category.category_id}`}
                  onClick={() => setSelectedCategory(category)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 transition-all flex items-center justify-between group ${
                    selectedCategory?.category_id === category.category_id
                      ? 'text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                  style={selectedCategory?.category_id === category.category_id ? { backgroundColor: 'var(--accent)' } : {}}
                >
                  <span className="text-sm truncate">{category.category_name}</span>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${
                    selectedCategory?.category_id === category.category_id ? 'rotate-90' : ''
                  }`} />
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Series Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-6 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h1 data-testid="series-page-title" className="text-2xl font-bold text-white">
                {selectedCategory ? selectedCategory.category_name : 'Select a Category'}
              </h1>
              <p data-testid="series-count-info" className="text-gray-500 text-sm mt-1">
                {selectedCategory 
                  ? `Showing ${displayedSeries.length} of ${sortedSeries.length} series` 
                  : 'Choose a category from the left to browse series'}
              </p>
            </div>

            {selectedCategory && allSeries.length > 0 && (
              <div data-testid="series-sort-controls" className="flex items-center gap-1 bg-[#1a1a1d] rounded-lg p-1">
                {SORT_OPTIONS.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    data-testid={`series-sort-${key}`}
                    onClick={() => setSortBy(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      sortBy === key
                        ? 'text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                    style={sortBy === key ? { backgroundColor: 'var(--accent)' } : {}}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div ref={scrollContainerRef} data-testid="series-scroll-container" className="flex-1 overflow-y-auto">
            {!selectedCategory ? (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <MonitorPlay className="w-20 h-20 text-gray-700 mb-4" />
                <p className="text-gray-500 text-lg">Select a category to view series</p>
                <p className="text-gray-600 text-sm mt-2">Browse {categories.length} categories</p>
              </div>
            ) : isLoadingSeries ? (
              <div data-testid="series-loading-spinner" className="flex justify-center py-20">
                <LoadingSpinner text="Loading series..." />
              </div>
            ) : displayedSeries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <MonitorPlay className="w-16 h-16 text-gray-700 mb-4" />
                <p className="text-gray-500">No series found in this category</p>
              </div>
            ) : (
              <div className="p-6">
                <div data-testid="series-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
                  {displayedSeries.map((item) => (
                    <ContentCard key={item.series_id} item={item} type="series" />
                  ))}
                </div>
                
                {hasMore && (
                  <div 
                    ref={loadMoreRef}
                    data-testid="series-load-more"
                    className="flex justify-center py-8"
                  >
                    {isLoadingMore ? (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Loading more...</span>
                      </div>
                    ) : (
                      <button
                        data-testid="series-load-more-btn"
                        onClick={loadMore}
                        className="px-6 py-2 text-white rounded-lg transition-colors hover:opacity-90" style={{backgroundColor:"var(--accent)"}}
                      >
                        Load More ({sortedSeries.length - displayedSeries.length} remaining)
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesPage;
